import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import { SUBMIT_CONTACT, GET_ADMIN_USERS } from '../../graphql/operations';
import CustomerModal from './CustomerModal';

interface CreateEnquiryModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (message: string) => void;
}

const CreateEnquiryModal: React.FC<CreateEnquiryModalProps> = ({ show, onHide, onSuccess }) => {
  const [submitContact, { loading: submitting }] = useMutation(SUBMIT_CONTACT);

  // Form Fields
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [message, setMessage] = useState('');

  const [selectedCustomerId, setSelectedCustomerIdState] = useState<string>('');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const { data: usersResult } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
    skip: !show,
  });
  const customers = React.useMemo(() => 
    (usersResult?.users || []).filter((u: any) => u.role === 'customer'),
    [usersResult]
  );

  // Validation & Error
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (show) {
      setSenderName('');
      setSenderEmail('');
      setSenderPhone('');
      setMessage('');
      setSelectedCustomerIdState('');
      setShowAddCustomerModal(false);
      setErrors({});
      setSubmitError('');
    }
  }, [show]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!senderName.trim()) {
      newErrors.name = 'Sender name is required';
    }

    if (!senderEmail.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(senderEmail)) {
      newErrors.email = 'Enter a valid email';
    }

    if (senderPhone.trim() && !/^\d{10}$/.test(senderPhone.replace(/[\s\-+]/g, '').slice(-10))) {
      newErrors.phone = 'Enter a valid 10-digit number';
    }

    if (!message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError('');

    try {
      const { data } = await submitContact({
        variables: {
          input: {
            name: senderName.trim(),
            email: senderEmail.trim(),
            phone: senderPhone.trim() || undefined,
            message: message.trim(),
            type: 'walk-in',
          },
        },
      });

      if (data?.submitContact?.success) {
        onSuccess(data.submitContact.message || 'Enquiry recorded successfully!');
        onHide();
      } else {
        setSubmitError(data?.submitContact?.message || 'Failed to record enquiry.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'An error occurred during submission.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton className="border-bottom pb-3" style={{ background: 'linear-gradient(135deg, #f8f9ff, #ffffff)' }}>
        <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>
          <i className="fas fa-envelope-open-text text-primary me-2" />
          Log New Customer Enquiry
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ padding: '24px' }}>
          {submitError && (
            <div className="alert alert-danger mb-4 py-2 px-3 rounded-3 border-0" style={{ fontSize: '13px' }}>
              <i className="fas fa-exclamation-triangle me-2" />
              {submitError}
            </div>
          )}

          <div className="mb-4">
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <span className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px', fontSize: '11px' }}>1</span>
              Sender Information
            </h6>
            
            <div className="bg-light rounded-4 p-3 border">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '13px' }}>
                      <span>Select Profile Customer</span>
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none fw-semibold" 
                        style={{ fontSize: '12px' }}
                        onClick={() => setShowAddCustomerModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i>New Profile Customer
                      </Button>
                    </Form.Label>
                    <Form.Select
                      value={selectedCustomerId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedCustomerIdState(val);
                        const selected = customers.find((c: any) => c.id === val);
                        if (selected) {
                          setSenderName(selected.username || '');
                          setSenderPhone(selected.phone || '');
                          setSenderEmail(selected.email || '');
                        } else {
                          setSenderName('');
                          setSenderPhone('');
                          setSenderEmail('');
                        }
                      }}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                    >
                      <option value="">-- Select Existing Customer (or Add New) --</option>
                      {customers.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.username} {c.companyName ? `(${c.companyName})` : ''} - {c.phone || 'No phone'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Sender Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Customer name"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Phone Number (Optional)</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={senderPhone}
                      onChange={(e) => setSenderPhone(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Email Address *</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="customer@example.com"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div>
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <span className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px', fontSize: '11px' }}>2</span>
              Enquiry Details
            </h6>

            <div className="bg-light rounded-4 p-3 border">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Message / Enquiry Text *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Type details of what the customer is asking or seeking information about..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      isInvalid={!!errors.message}
                    />
                    <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <><Spinner size="sm" animation="border" className="me-2" />Recording...</>
            ) : (
              <><i className="fas fa-save me-2" />Record Enquiry</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
      <CustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onCustomerSaved={(savedCustomer) => {
          setSelectedCustomerIdState(savedCustomer.id);
          setSenderName(savedCustomer.username || '');
          setSenderPhone(savedCustomer.phone || '');
          setSenderEmail(savedCustomer.email || '');
        }}
      />
    </Modal>
  );
};

export default CreateEnquiryModal;
